// Browser-side storage for one student in one live quiz. The running total is
// summed here rather than fetched: studentState is a single broadcast shared by
// every student on the channel, so the server has no per-student answer to give.

const TOKEN_KEY = 'quiz.participantToken';
const START_KEY = 'quiz.questionStart';
const scoreKey = (joinCode) => `quiz.score.${joinCode}`;

const readJSON = (key, fallback) => {
    try {
        const held = localStorage.getItem(key);
        return held === null ? fallback : JSON.parse(held);
    } catch {
        return fallback;
    }
};

const writeJSON = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Storage blocked (private mode). Every caller here is a convenience,
        // never the only record of anything, so they work on without it.
    }
};

// crypto.randomUUID is only exposed in a secure context, and the classroom case
// is exactly the insecure one: a phone on http://<lan-ip>:3000. getRandomValues
// carries no such restriction, so a v4 is assembled by hand where it is missing.
const newToken = () => {
    if (crypto.randomUUID) return crypto.randomUUID();

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Minted before the request rather than read off the response: two joins that
// overlap — StrictMode's double-invoke, or a double tap — would both find an
// empty slot and come back as two different students. Writing the token first
// means they carry the same one, and the server's ON CONFLICT folds them into
// one participant. Replaying it later is also what makes a refresh a no-op
// that comes back with the same name.
export const participantToken = () => {
    const held = localStorage.getItem(TOKEN_KEY);
    if (held) return held;

    const minted = newToken();
    localStorage.setItem(TOKEN_KEY, minted);
    return minted;
};

export const saveParticipantToken = (token) => localStorage.setItem(TOKEN_KEY, token);

// Date.now(), not performance.now(): the latter restarts on reload, which this
// has to survive. Never mints a "now" — an unknown start would read as the
// fastest answer possible.
export const questionStartedAt = (joinCode, ord) => {
    const held = readJSON(START_KEY, null);
    return held?.joinCode === joinCode && held.ord === ord ? held.startedAt : null;
};

// Writing only when nothing is held is what makes a reload keep the original
// start — and what makes StrictMode's double-invoke a non-event.
export const noteQuestionShown = (joinCode, ord) => {
    if (questionStartedAt(joinCode, ord) !== null) return;
    writeJSON(START_KEY, { joinCode, ord, startedAt: Date.now() });
};

const storedScores = (joinCode) => readJSON(scoreKey(joinCode), {});

const sumScores = (scores) => Object.values(scores).reduce((sum, points) => sum + points, 0);

// Guarded per ord so a repeat reveal cannot count the same question twice.
export const recordQuestionScore = (joinCode, ord, points) => {
    const scores = storedScores(joinCode);
    if (!(ord in scores)) {
        scores[ord] = points;
        writeJSON(scoreKey(joinCode), scores);
    }
    return sumScores(scores);
};

export const storedTotalScore = (joinCode) => sumScores(storedScores(joinCode));
